import { useEffect, useMemo } from 'react';
import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { automationApi, ApiError } from '@/lib/api';
import { showApiError } from '@/lib/errors';
import { getLimitErrorMessage, isLimitExceededError } from '@/hooks/useFeatureLimits';

export interface AutomationFlow {
  id: string;
  name: string;
  description?: string;
  triggerType?: string;
  triggerConfig?: Record<string, any>;
  flowData?: FlowDefinition;
  businessAccountId: string;
  userId: string;
  status: 'DRAFT' | 'ACTIVE' | 'PAUSED';
  createdAt: string;
  updatedAt: string;
  businessAccount?: {
    id: string;
    name: string;
    phoneNumber?: string;
  };
  user?: {
    id: string;
    name: string;
    email: string;
  };
  // Execution stats included in the response
  stats?: {
    totalRuns: number;
    successRate: number;
    lastRun: string | null;
  };
  // Flat stats properties (API compatibility)
  totalRuns?: number;
  successRate?: number | string;
  lastRun?: string | null;
}

export interface TriggerConfig {
  type: 'message_received' | 'keyword' | 'tag_added' | 'time_based' | 'webhook';
  conditions: Record<string, any>;
}

// For API compatibility
export type TriggerConfigData = Record<string, any>;

export interface FlowDefinition {
  nodes: FlowNode[];
  edges: FlowEdge[];
}

export interface FlowNode {
  id: string;
  type: string;
  data: Record<string, any>;
  position: { x: number; y: number };
}

export interface FlowEdge {
  id: string;
  source: string;
  target: string;
  type?: string;
}

export function useAutomationFlows(params?: {
  businessAccountId?: string;
  status?: string;
  search?: string;
  triggerType?: string;
  page?: number;
  limit?: number;
}) {
  return useQuery({
    queryKey: [
      'automation-flows',
      params?.businessAccountId,
      params?.status,
      params?.search,
      params?.triggerType,
      params?.page,
      params?.limit,
    ],
    queryFn: async () => {
      const {
        businessAccountId,
        status,
        search,
        triggerType,
        page = 1,
        limit = 12,
      } = params || {};
      const safePage = Math.max(1, Number(page) || 1);
      const safeLimit = Math.min(Math.max(Number(limit) || 12, 1), 1000);
      const offset = (safePage - 1) * safeLimit;

      const result = await automationApi.getAll(
        businessAccountId,
        status,
        search,
        safeLimit,
        offset,
        triggerType,
      );

      if (result.error) {
        throw ApiError.fromResult(result);
      }

      // Deduplicate by id — Dynamo/MySQL shadow paths can theoretically surface
      // the same flow twice; React keys + pagination counts must stay unique.
      const seen = new Set<string>();
      const data = (result.data || []).filter((flow: { id?: string }) => {
        if (!flow?.id || seen.has(flow.id)) return false;
        seen.add(flow.id);
        return true;
      });

      // Return both data and count for pagination
      return {
        data,
        count: Number(result.count) || 0,
      };
    },
    enabled: !!params?.businessAccountId,
    placeholderData: keepPreviousData, // Keep showing old results while new search loads
    staleTime: 30 * 1000, // 30s — search results should refresh reasonably fast
    gcTime: 5 * 60 * 1000, // Cache for 5 minutes
    refetchOnMount: true, // Refetch on mount for fresh data
    refetchOnWindowFocus: false, // Don't refetch on window focus
  });
}

/**
 * Page size used when walking the whole `/automation/flows` list.
 *
 * The endpoint accepts up to 1000 rows in one shot, and the surfaces that need
 * "every flow" (KPI rail, overview drawer, trigger dropdown, Runs fan-out) used
 * to ask for exactly that — one very large response, and a hard truncation at
 * row 1000 with no way to tell that it happened. Paging in bounded chunks keeps
 * each response small, lets the first page paint immediately, and makes the
 * ceiling explicit (`isTruncated`) instead of silent.
 */
export const ALL_FLOWS_PAGE_SIZE = 100;

/**
 * Safety ceiling on how many flows the paged walk will accumulate. Matches the
 * previous single-request cap so coverage is unchanged; raise it only with a
 * caller that can actually cope with the extra rows.
 */
export const ALL_FLOWS_MAX = 1000;

/** One page of the paged walk, as returned by the query function. */
export type AutomationFlowsPage = {
  rows: AutomationFlow[];
  /** Server-side COUNT(*) for the query's filters — independent of the page size. */
  count: number;
  /** Offset this page was requested at. */
  offset: number;
};

/**
 * Offset of the next page, or `undefined` when the walk is done.
 *
 * Two independent stop conditions, and BOTH are load-bearing:
 *   - an empty page means the server has nothing more, whatever `count` claims
 *     (a stale/inflated count would otherwise loop forever);
 *   - the accumulated offset reaching `count` or `maxFlows` is the normal end.
 */
export function nextFlowsPageParam(
  lastPage: AutomationFlowsPage,
  maxFlows: number = ALL_FLOWS_MAX,
): number | undefined {
  if (!lastPage?.rows?.length) return undefined;
  const nextOffset = lastPage.offset + lastPage.rows.length;
  if (nextOffset >= Math.min(lastPage.count, maxFlows)) return undefined;
  return nextOffset;
}

/**
 * Flatten collected pages into the `{ data, count }` envelope callers expect.
 *
 * Deduplicates by id: Dynamo/MySQL shadow paths can surface the same flow
 * twice, and a row inserted between two page requests shifts the offset window
 * so a row can legitimately repeat across pages.
 */
export function mergeFlowPages(pages: AutomationFlowsPage[]): {
  data: AutomationFlow[];
  count: number;
} {
  const seen = new Set<string>();
  const rows: AutomationFlow[] = [];
  for (const page of pages || []) {
    for (const flow of page?.rows || []) {
      if (!flow?.id || seen.has(flow.id)) continue;
      seen.add(flow.id);
      rows.push(flow);
    }
  }
  return { data: rows, count: Number(pages?.[pages.length - 1]?.count) || 0 };
}

export type UseAllAutomationFlowsResult = {
  /** Same `{ data, count }` envelope `useAutomationFlows` returns, or undefined before the first page settles. */
  data?: { data: AutomationFlow[]; count: number };
  /** True until the FIRST page arrives. */
  isLoading: boolean;
  /** True until EVERY page has arrived — use this to gate anything that needs the complete set. */
  isLoadingAll: boolean;
  isFetching: boolean;
  /** True once the first page settled successfully — `count` is a server-side COUNT(*), so it is exact from page 1. */
  isSuccess: boolean;
  /** True when every page has been collected without error. */
  isComplete: boolean;
  /**
   * True when the settled walk collected fewer rows than the server's count —
   * i.e. `ALL_FLOWS_MAX` was hit (or the count moved mid-walk). Surfaces the
   * ceiling the single 1000-row request used to hide.
   */
  isTruncated: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
};

/**
 * Fetch every automation flow for a business account by PAGING the list
 * endpoint, instead of requesting one oversized page.
 *
 * Consumers get the same `{ data, count }` shape `useAutomationFlows` returns,
 * so a caller can swap between the two without reshaping anything. `count` is
 * the server's COUNT(*) for the query, so it is exact as soon as page 1 lands —
 * badges that only need the total do not have to wait for the walk to finish.
 */
export function useAllAutomationFlows(params?: {
  businessAccountId?: string;
  status?: string;
  search?: string;
  triggerType?: string;
  /** Rows per request. Clamped to 1..ALL_FLOWS_PAGE_SIZE. */
  pageSize?: number;
  /** Hard ceiling on accumulated rows. Clamped to 1..ALL_FLOWS_MAX. */
  maxFlows?: number;
  /** Extra gate on top of the businessAccountId requirement. */
  enabled?: boolean;
}): UseAllAutomationFlowsResult {
  const businessAccountId = params?.businessAccountId;
  const status = params?.status;
  const search = params?.search;
  const triggerType = params?.triggerType;
  const pageSize = Math.min(Math.max(Number(params?.pageSize) || ALL_FLOWS_PAGE_SIZE, 1), ALL_FLOWS_PAGE_SIZE);
  const maxFlows = Math.min(Math.max(Number(params?.maxFlows) || ALL_FLOWS_MAX, 1), ALL_FLOWS_MAX);
  const enabled = params?.enabled !== false && !!businessAccountId;

  const query = useInfiniteQuery({
    queryKey: [
      'automation-flows-all',
      businessAccountId,
      status,
      search,
      triggerType,
      pageSize,
      maxFlows,
    ],
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const offset = Number(pageParam) || 0;
      // Never ask for more than the remaining budget — the last page is short.
      const limit = Math.min(pageSize, Math.max(maxFlows - offset, 1));

      const result = await automationApi.getAll(
        businessAccountId,
        status,
        search,
        limit,
        offset,
        triggerType,
      );

      if (result.error) {
        throw ApiError.fromResult(result);
      }

      return {
        rows: (result.data || []) as AutomationFlow[],
        // Server-side COUNT(*) for these filters — independent of the page size.
        count: Number(result.count) || 0,
        offset,
      };
    },
    getNextPageParam: (lastPage) => nextFlowsPageParam(lastPage, maxFlows),
    enabled,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });

  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;

  // Walk the remaining pages automatically — callers want the whole list, not a
  // "load more" affordance.
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const pages = query.data?.pages;

  const data = useMemo(() => (pages ? mergeFlowPages(pages) : undefined), [pages]);

  const isComplete = Boolean(pages) && !hasNextPage && !isFetchingNextPage;

  return {
    data,
    isLoading: query.isLoading,
    isLoadingAll: query.isLoading || hasNextPage || isFetchingNextPage,
    isFetching: query.isFetching,
    isSuccess: query.isSuccess,
    isComplete,
    isTruncated: isComplete && (data?.count ?? 0) > (data?.data.length ?? 0),
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

export function useCreateAutomationFlow() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (flowData: {
      businessAccountId: string;
      name: string;
      description?: string;
      triggerType: string;
      triggerConfig: TriggerConfigData;
      flowData: FlowDefinition;
      settings?: Record<string, any>;
      userId: string;
    }) => {
      const result = await automationApi.create({
        businessAccountId: flowData.businessAccountId,
        name: flowData.name,
        description: flowData.description,
        triggerType: flowData.triggerType,
        triggerConfig: flowData.triggerConfig,
        flowData: flowData.flowData,
        userId: flowData.userId,
      });

      if (result.error) {
        throw ApiError.fromResult(result);
      }

      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['automation-flows'] });
      toast({ variant: "success",
        title: "Automation flow created",
        description: "Your automation flow has been successfully created.",
      });
    },
    onError: (error: any) => {
      const errorMessage = getLimitErrorMessage(error);
      toast({
        title: isLimitExceededError(error) ? "Limit Reached" : "Error creating automation flow",
        description: errorMessage,
        variant: "destructive",
        duration: isLimitExceededError(error) ? 8000 : 5000,
      });
    }
  });
}

/**
 * Create a flow and immediately publish it.
 *
 * `automationApi.create` hardcodes `status: 'DRAFT'`, which is right for the
 * builder (you save as you go) and wrong for the ice breaker editor: an ice
 * breaker only fires an ACTIVE flow, and the picker deliberately offers nothing
 * else — so creating a DRAFT there would hand the user a flow the very next
 * dropdown refuses to show.
 *
 * Deliberately toasts nothing. The caller owns the messaging because it knows
 * what the flow was created FOR, and reusing `useCreateAutomationFlow` here
 * would fire its generic success toast on top of the caller's.
 */
export function useCreateActiveAutomationFlow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      businessAccountId: string;
      userId: string;
      name: string;
      description?: string;
      triggerType: string;
      triggerConfig: Record<string, unknown>;
      flowData: FlowDefinition;
    }) => {
      const created = await automationApi.create({
        businessAccountId: input.businessAccountId,
        userId: input.userId,
        name: input.name,
        description: input.description,
        triggerType: input.triggerType,
        triggerConfig: input.triggerConfig,
        flowData: input.flowData,
      });

      // apiClient swallows failures into the result object rather than throwing,
      // so a bare try/catch here would never fire. Entitlement limits (flow
      // count) surface through this branch.
      if (created.error || (created as { success?: boolean }).success === false) {
        throw ApiError.fromResult(created, 'Could not create the flow');
      }

      const flow = (created.data ?? created) as { id?: string; name?: string };
      if (!flow?.id) {
        throw new ApiError('The flow was created but returned no id');
      }

      const published = await automationApi.publish(flow.id);
      if (published.error || (published as { success?: boolean }).success === false) {
        // The flow exists but is still DRAFT. Surface it rather than returning a
        // flow the ice breaker picker will not offer — silently linking an
        // unpublished flow produces a chip that does nothing when tapped.
        throw ApiError.fromResult(
          published,
          'The flow was created but could not be activated',
        );
      }

      return { id: flow.id, name: flow.name ?? input.name, status: 'ACTIVE' as const };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['automation-flows'] });
    },
  });
}

export function useUpdateAutomationFlow() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<AutomationFlow> }) => {
      const result = await automationApi.update(id, updates);

      if (result.error) {
        throw ApiError.fromResult(result);
      }

      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['automation-flows'] });
      toast({ variant: "success",
        title: "Automation flow updated",
        description: "Your automation flow has been updated successfully.",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to update automation flow. Please try again.",
        variant: "destructive",
      });
    },
  });
}

export function useExecuteAutomationFlow() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ flowId, contactId, triggerData, dryRun = true }: { flowId: string; contactId: string; triggerData?: any; dryRun?: boolean }) => {
      const result = await automationApi.execute(flowId, { contactId, triggerData, dryRun });

      if (result.error) {
        throw ApiError.fromResult(result);
      }

      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['automation-flows'] });
      toast({ variant: "success",
        title: "Automation flow executed",
        description: "Your automation flow has been executed successfully.",
      });
    },
    onError: (error) => {
      showApiError(error, { fallback: { title: "Error executing automation flow" } });
    }
  });
}

export function useAutomationFlow(flowId?: string) {
  return useQuery({
    queryKey: ['automation-flow', flowId],
    queryFn: async () => {
      if (!flowId) return null;

      const result = await automationApi.getById(flowId);

      if (result.error) {
        throw ApiError.fromResult(result);
      }

      return result.data;
    },
    enabled: !!flowId,
    staleTime: 15000, // Data stays fresh for 15 seconds
    gcTime: 2 * 60 * 1000, // Cache for 2 minutes
  });
}

export function useFlowExecutions(flowId?: string) {
  return useQuery({
    queryKey: ['flow-executions', flowId],
    queryFn: async () => {
      if (!flowId) return [];

      const result = await automationApi.getExecutions(flowId);

      if (result.error) {
        throw ApiError.fromResult(result);
      }

      return result.data;
    },
    enabled: !!flowId,
    staleTime: 10000, // Data stays fresh for 10 seconds
    gcTime: 2 * 60 * 1000, // Cache for 2 minutes
  });
}

export function useFlowExecutionSummaries(flowIds: string[]) {
  return useQuery({
    queryKey: ['flow-execution-summaries', flowIds],
    queryFn: async () => {
      if (flowIds.length === 0) return [];

      const result = await automationApi.getExecutionSummaries(flowIds);

      return result;
    },
    enabled: flowIds.length > 0,
    staleTime: 30000, // Data stays fresh for 30 seconds
    gcTime: 5 * 60 * 1000, // Cache for 5 minutes
    refetchOnMount: false, // Don't refetch if we have cached data
    refetchOnWindowFocus: false, // Don't refetch on window focus
  });
}

export function useActivateFlow() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (flowId: string) => {
      const result = await automationApi.publish(flowId);

      if (result.error) {
        throw ApiError.fromResult(result);
      }

      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['automation-flows'] });
      toast({ variant: "success",
        title: "Flow activated",
        description: "Your automation flow has been activated successfully.",
      });
    },
    onError: (error) => {
      showApiError(error, { fallback: { title: "Error activating flow" } });
    }
  });
}

export function usePauseFlow() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (flowId: string) => {
      const result = await automationApi.pause(flowId);

      if (result.error) {
        throw ApiError.fromResult(result);
      }

      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['automation-flows'] });
      toast({ variant: "success",
        title: "Flow paused",
        description: "Your automation flow has been paused successfully.",
      });
    },
    onError: (error) => {
      showApiError(error, { fallback: { title: "Error pausing flow" } });
    }
  });
}

export function useResumeFlow() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (flowId: string) => {
      const result = await automationApi.resume(flowId);

      if (result.error) {
        throw ApiError.fromResult(result);
      }

      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['automation-flows'] });
      toast({ variant: "success",
        title: "Flow resumed",
        description: "Your automation flow has been resumed successfully.",
      });
    },
    onError: (error) => {
      showApiError(error, { fallback: { title: "Error resuming flow" } });
    }
  });
}


export function useDuplicateFlow() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      flowId,
      name,
      businessAccountId,
      userId
    }: {
      flowId: string;
      name: string;
      businessAccountId: string;
      userId: string;
    }) => {
      const result = await automationApi.duplicate(flowId, {
        name,
        businessAccountId,
        userId
      });

      if (result.error) {
        throw ApiError.fromResult(result);
      }

      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['automation-flows'] });
      toast({ variant: "success",
        title: "Flow duplicated",
        description: "Your automation flow has been duplicated successfully.",
      });
    },
    onError: (error) => {
      showApiError(error, { fallback: { title: "Error duplicating flow" } });
    }
  });
}

export function useDeleteAutomationFlow() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (flowId: string) => {
      const result = await automationApi.delete(flowId);

      if (result.error) {
        throw ApiError.fromResult(result);
      }

      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['automation-flows'] });
      toast({ variant: "success",
        title: "Flow deleted",
        description: "Automation flow deleted successfully.",
      });
    },
    onError: (error) => {
      showApiError(error, { fallback: { title: "Couldn't delete flow" } });
    }
  });
}
